import React from "react"
import { Link } from "react-router-dom"
import { getDateColDef } from "components/grids/columnTemplates"
import { intersperse } from "utilities/misc"

// apis
import { useGetStudyActivities } from "api/hooks/studies/useActivitiesApi"

export default function useActivitiesColDefs(studyId, ongoingOnly) {
  const { data: activities = [] } = useGetStudyActivities(studyId)
  const columnDefs = React.useMemo(() => {
    const columns = [
      {
        headerName: "Activity",
        field: "project_title",
        cellRenderer: ({ data }) => (
          <Link
            className="activity-link"
            to={`/study/${studyId}/activity/${data.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {data.project_title}
          </Link>
        ),
        filterParams: {
          caseSensitive: true,
        },
      },
      {
        headerName: "Category",
        field: "project_category",
      },
      {
        headerName: "Registry",
        field: "registry_name",
        cellRenderer: "RegistryCellRenderer",
        cellClass: ["col-badge"],
      },
      {
        headerName: "Step",
        field: "curr_state",
        suppressSizeToFit: true,
        cellRenderer: "WorkflowStatusBadge",
        cellRendererParams: {
          list: activities,
        },
        cellClass: ["col-badge"],
      },
      {
        headerName: "Due Date",
        field: "project_due_date",
        ...getDateColDef({ gridIsControlled: true }),
      },
      {
        headerName: "Projected Completion Date",
        field: "projected_completion_date",
        ...getDateColDef({ gridIsControlled: true }),
      },
    ]
    // Actual completion date added to Activities grid, but not to Overview tab grid, which only shows ongoing activities
    if (!ongoingOnly) {
      columns.push({
        headerName: "Actual Completion Date",
        field: "actual_completion_date",
        ...getDateColDef({ gridIsControlled: true }),
      })
    }
    columns.push(
      {
        headerName: "Assessment",
        field: "attached_assessments",
        valueGetter: (params) => {
          return params.data?.attached_assessments?.map((a) => a.assessment_name) || []
        },
        cellRenderer: (params) => {
          const assessments = params.value || []
          return (
            <div className="attached-assessments-pill-container">
              {intersperse(
                assessments.map((name, index) => (
                  <Link
                    key={index}
                    className="prime-link"
                    to={`/study/${studyId}/assessment/${params.data.attached_assessments[index].assessment_pk}`}
                  >
                    {name}
                  </Link>
                )),
                ", "
              )}
            </div>
          )
        },
      },
      {
        headerName: "Risk",
        field: "status",
        cellRenderer: "StatusCellRenderer",
      },
      {
        headerName: "As Of Date",
        field: "last_action_time",
        ...getDateColDef({ gridIsControlled: true }),
      }
    )
    return columns
  }, [activities])

  return { columnDefs }
}
